import { Component, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import {
  ModalOpenerService,
  BookerType,
  LogonService,
  UserService,
  WebTokenService
} from '@sabstravtech/obtservices/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  LoginResponse,
  AuthInfo,
  LoginError,
  LoginErrorReason,
  RegExps,
  LoginErrorData
} from '@sabstravtech/obtservices/base';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

import { BaseComponent } from '../../core/components/base_components/base-componet';
import { LightningModalTypes } from '../../core/classes/modal-types.enum';
import { VERSION } from '../../../environments/version';
import { ThemeService } from '../../core/services/theme.service';
// import { AuthService } from '@auth0/auth0-angular';

@Component({
  selector: 'app-logon',
  templateUrl: './logon.component.html',
  styleUrls: ['./logon.component.scss'],
  standalone: false
})
export class LogonComponent extends BaseComponent implements OnInit {
  login_error = false;
  error_message = '';
  user_name = '';
  pass_word = '';
  loading = false;
  token = null;
  overwrite = '';
  loginSub: Subscription = new Subscription();
  loginAnnouncements: any;
  currentVersion = VERSION;
  username_error = false;
  password_error = false;
  showDigitsURL = false;
  hasSSOMessage = false;
  showPassword = false;
  inputType = 'password';
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  constructor(
    private modalService: ModalOpenerService,
    private router: Router,
    private logonService: LogonService,
    protected themeService: ThemeService,
    // private announcementService: AnnouncementService,
    private userService: UserService,
    private webTokenService: WebTokenService,
    // public auth: AuthService,
    title: Title,
    public translateService: TranslateService
  ) {
    super(title, translateService);
  }

  private getHashQueryParams(): URLSearchParams {
    const hash = window.location.hash; // e.g. #/logon?jwt=TOKEN&redirect_url=/itinerary/123
    const queryIndex = hash.indexOf('?');
    if (queryIndex === -1) {
      return new URLSearchParams();
    }
    return new URLSearchParams(hash.substring(queryIndex));
  }

  private handleRedirectOrDefault(defaultRoute: string | string[]): void {
    // Parse from hash fragment (hash routing) and fallback to search params
    const hashParams = this.getHashQueryParams();
    const searchParams = new URLSearchParams(window.location.search);
    const redirectUrl = hashParams.get('redirect_url') || searchParams.get('redirect_url');

    if (redirectUrl) {
      try {
        const decodedUrl = decodeURIComponent(redirectUrl);

        // Ensure the redirect is a safe internal route (starts with '/')
        if (decodedUrl.startsWith('/')) {
          console.log(`+++ Navigating to: ${decodedUrl} +++`);
          // navigateByUrl (not navigate([decodedUrl])) so any query string on
          // decodedUrl (e.g. /results/rail?results=<uuid>) is parsed into real
          // query params rather than treated as a literal path segment.
          this.router.navigateByUrl(decodedUrl);
          return;
        } else {
          console.warn('Blocked unsafe redirect attempt:', decodedUrl);
        }
      } catch (err) {
        console.error('Error decoding redirect URL:', err);
      }
    }
    this.router.navigate(Array.isArray(defaultRoute) ? defaultRoute : [defaultRoute]);
  }

  ngOnInit(): void {
    // Handle JWT token from query param (email booking links)
    // Hash routing: #/logon?jwt=TOKEN&redirect_url=... — parse from hash fragment
    const hashParams = this.getHashQueryParams();
    const jwtFromUrl = hashParams.get('jwt');

    if (jwtFromUrl) {
      console.log('+++ JWT token found in URL, initialising session +++');
      this.webTokenService.jwtToken = jwtFromUrl;

      // Decode the JWT payload to get user data for session initialisation.
      // The JWT contains the full auth payload (username, user_id, office_id, etc.)
      // so we can hydrate the user session without calling /refreshToken (no cookie).
      try {
        const payload = JSON.parse(atob(jwtFromUrl.split('.')[1]));
        this.userService.setUser(payload);
        this.logonService.isLoggedIn = true;
      } catch (e) {
        console.error('Failed to decode JWT from URL:', e);
      }

      const redirectUrl = hashParams.get('redirect_url');
      if (redirectUrl?.startsWith('/')) {
        // See handleRedirectOrDefault: navigateByUrl preserves query params
        // (e.g. /results/rail?results=<uuid>) that navigate([...]) would drop.
        this.router.navigateByUrl(redirectUrl);
      } else {
        this.router.navigate(['/main']);
      }
      return;
    }

    if (!window.location.search.includes('code=')) {
      this.logonService.checkLogin().then((isLoggedin: boolean) => {
        if (isLoggedin) {
          this.handleRedirectOrDefault('/main');
        } // get the logon announcements
        this.setTitle(
          this.translateService.instant(
            `logon ${this.themeService.isLightningEnv ? ' - LightUk' : ''}`
          )
        );
      });
    }
  }

  isEmptyObject(obj: any): boolean {
    return obj && Object.keys(obj).length === 0;
  }

  login(again?: boolean): void {
    this.login_error = false;
    this.username_error = false;
    this.password_error = false;
    // this.userService.onBehalfUser = undefined;
    this.error_message = 'Please enter a username and password';

    if (this.user_name.length < 1 && this.pass_word.length < 1) {
      // If both are missing, add borders to both boxes
      this.password_error = true;
      this.username_error = true;
      this.login_error = true;
      return;
    }

    if (this.user_name.length < 1) {
      this.login_error = true;
      this.username_error = true;
      return;
    }

    if (this.pass_word.length < 1) {
      this.login_error = true;
      this.password_error = true;
      return;
    }

    this.error_message = '';
    this.loading = true;

    if (this.loginSub) {
      this.loginSub.unsubscribe();
    }

    this.loginSub = this.subscribe(
      this.logonService.login(this.user_name, this.pass_word, again),
      () => {
        this.loading = false;
        this.handleRedirectOrDefault('/search');
      },
      this.handleLoginError.bind(this)
    );
  }

  loadGoogleMapsApi() {
    const script = document.createElement('script');
    script.src =
      'https://maps.googleapis.com/maps/api/js?key=AIzaSyC_I3019IO-Dzdx5Hn-7Hlp1socVWLYeno&libraries=places';
    script.async = true;
    document.head.appendChild(script);
  }

  loginAgainModal(): void {
    this.modalService
      .open(LightningModalTypes.ModalComponent, { centered: true, backdrop: 'static' }, {})
      .then((success) => {
        if (success) {
          this.login(true);
        }
      });
  }

  passwordExpiryModal(errorData: LoginResponse) {
    const authInfo: AuthInfo = new AuthInfo();
    Object.assign(authInfo, {
      username: this.logonService.authInfo.username,
      token: errorData.changeToken
    });
    const modalValues = {
      errorData: errorData,
      authInfo
    };
    this.modalService
      .open(LightningModalTypes.ModalPasswordExpiryComponent, {}, modalValues)
      .then((authInfo2: AuthInfo) => {
        if (authInfo2) {
          console.log(authInfo2);
          this.user_name = authInfo2.username;
          this.pass_word = authInfo2.password;
          this.login();
        }
      });
  }

  handleLoginError(error: LoginError): void {
    if (error.error === 'Authentication failed; User password is expired') {
      this.expiredPassword();
    }

    if (error.name === 'LoginError') {
      switch (error.reason) {
        case LoginErrorReason.LOGED_IN:
          this.loginAgainModal();
          break;
        case LoginErrorReason.PASSWORD_EXPIRED:
          this.passwordExpiryModal(error.data as LoginResponse);
          break;
        case LoginErrorReason.AUTH_ERROR:
          this.error_message = error.message;
          console.log('errororor', this.error_message);
          this.login_error = true;
          break;
        case LoginErrorReason.SSO:
          this.hasSSOMessage = true;
          this.showDigitsURL = !!error.data;
          break;
        case LoginErrorReason.MFA_REQUIRED:
          this.handleMfaRequired(error.data as LoginResponse);
          break;
      }
    } else if (
      error.statusText === 'Unauthorized' &&
      error.error !== 'Authentication failed; User password is expired'
    ) {
      this.error_message = 'Invalid username or password. Please try again';
      console.warn(error.error);
      this.login_error = true;
    } else if (
      typeof error.error === 'object' &&
      error.error?.type === 'usernameAuthMethodsFalse'
    ) {
      this.error_message = error.error.message;
      this.login_error = true;
    }
    this.loading = false;
  }

  /**
    @desc - close the error box
  **/
  closeErrors(): void {
    this.login_error = false;
    this.password_error = false;
    this.username_error = false;
  }

  /**
    @desc - attempt to recover the users password if a valid email supplied
  **/
  recoverPassword(expiredPassword?: boolean): void {
    this.username_error = false;
    this.password_error = false;
    this.login_error = false;
    if (new RegExp(RegExps.EMAIL_VALIDATOR).test(this.user_name)) {
      this.subscribe(
        this.logonService.recoverPassword(this.user_name),
        () => {
          this.modalService.open(
            LightningModalTypes.ModalRecoverPasswordComponent,
            { centered: true },
            { expiredPassword: expiredPassword }
          );
        },
        (error) => {
          this.error_message =
            error?.status === 429
              ? error.error
              : 'An unknown error occured calling recover password, please try again.';
          this.login_error = true;
          this.loading = false;
        }
      );
    } else {
      this.error_message = 'Please supply a valid email address.';
      this.login_error = true;
      this.username_error = true;
      this.loading = false;
    }
  }

  /**
    @desc - attempt to recover the users password if a expided email supplied
  **/
  expiredPassword(): void {
    this.modalService
      .open(LightningModalTypes.ModalExpiredPasswordComponent, { centered: true }, {})
      .then((success) => {
        if (success) {
          this.recoverPassword(true);
        }
      });
  }

  /**
    @desc - handle MFA verification when required by the server
  **/
  handleMfaRequired(loginResponse: LoginResponse): void {
    const modalValues = {
      userId: loginResponse.user_id,
      userEmail: this.user_name
    };
    this.modalService
      .open(
        LightningModalTypes.MfaCodeComponent,
        { centered: true, backdrop: 'static', keyboard: false },
        modalValues
      )
      .then((result: any) => {
        if (result?.success) {
          this.handleRedirectOrDefault('/search');
        }
      })
      .catch(() => {
        // User cancelled MFA - stay on login page
      });
  }

  getTotalNoOfAnnouncements(): number {
    return 0;
    // return this.announcementService.getTotalNoOfAnnouncements();
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
    this.inputType = this.showPassword ? 'text' : 'password';
  }

  // authSso() {
  //   this.auth.loginWithRedirect();
  // }
}
